import { Component, computed, effect, ElementRef, inject, untracked, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { AgentClientService } from '../../services/agent-client.service';
import { AgentSessionStore } from '../../services/agent-session.store';
import { AgentWorkspaceRegistry } from '../../services/agent-workspace-registry.service';
import { AgentUiStore } from '../../services/agent-ui.store';
import { AgentMarkdownPipe } from './agent-markdown.pipe';
import { AgentChoice, AgentDiagnostic } from '../../interfaces/agent-session';

interface AgentDiagnosticBatch {
  readonly requestId: string;
  readonly diagnostics: readonly AgentDiagnostic[];
}

export function diagnosticDuration(diagnostic: AgentDiagnostic): number | undefined {
  const value = diagnostic.details['duration_ms'];
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;
}

export function formatDiagnosticDuration(durationMs: number | undefined): string {
  if (durationMs === undefined) return '';
  if (durationMs < 1000) return `${Math.round(durationMs)} ms`;
  if (durationMs < 60_000) return `${(durationMs / 1000).toFixed(durationMs < 10_000 ? 2 : 1)} s`;
  const minutes = Math.floor(durationMs / 60_000);
  const seconds = ((durationMs % 60_000) / 1000).toFixed(1);
  return `${minutes}m ${seconds}s`;
}

/** Legacy parser retained only to prove numbered prose never becomes an action. */
export function numberedChoices(_text: string): never[] { return []; }

@Component({
  selector: 'agent-dock', standalone: true, imports: [FormsModule, MatButtonModule, AgentMarkdownPipe],
  templateUrl: './agent-dock.component.html', styleUrl: './agent-dock.component.scss'
})
export class AgentDockComponent {
  readonly workspaces = inject(AgentWorkspaceRegistry);
  readonly sessions = inject(AgentSessionStore);
  readonly client = inject(AgentClientService);
  private readonly ui = inject(AgentUiStore);
  readonly session = computed(() => this.sessions.sessions().get(this.workspaces.activeId()));
  readonly transcript = viewChild<ElementRef<HTMLElement>>('transcript');
  readonly composer = viewChild<ElementRef<HTMLTextAreaElement>>('composer');
  private followOutput = true;
  private previousScope = '';

  constructor() {
    effect(() => {
      const id = this.workspaces.activeId();
      untracked(() => this.sessions.ensure(id));
    }, { allowSignalWrites: true });
    effect(() => {
      this.session();
      this.ui.open();
      const id = this.workspaces.activeId();
      if (this.previousScope !== id) { this.followOutput = true; this.previousScope = id; }
      const element = this.transcript()?.nativeElement;
      if (element && this.followOutput) requestAnimationFrame(() => element.scrollTop = element.scrollHeight);
    });
  }

  setDraft(draft: string): void {
    this.sessions.update(this.workspaces.activeId(), session => ({ ...session, draft }));
  }

  async selectAttachments(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files) await this.client.addAttachments(this.workspaces.activeId(), Array.from(input.files));
    input.value = '';
  }

  removeAttachment(index: number): void { this.client.removeAttachment(this.workspaces.activeId(), index); }

  diagnosticText(details: Record<string, unknown>): string { return JSON.stringify(details, null, 2); }

  diagnosticDuration(diagnostic: AgentDiagnostic): number | undefined { return diagnosticDuration(diagnostic); }
  formatDuration(durationMs: number | undefined): string { return formatDiagnosticDuration(durationMs); }

  requestDuration(batch: AgentDiagnosticBatch): number | undefined {
    const terminal = [...batch.diagnostics].reverse().find(item => item.stage === 'runtime'
      && (item.status === 'completed' || item.status === 'failed'));
    return terminal ? diagnosticDuration(terminal) : undefined;
  }

  totalDuration(): number | undefined {
    const benchmark = [...(this.session()?.diagnostics ?? [])].reverse().find(item => item.stage === 'benchmark'
      && item.details['operation'] === 'agent_workflow');
    return benchmark ? diagnosticDuration(benchmark) : undefined;
  }

  /** Groups the session history by request so older traces remain available without overwhelming the transcript. */
  diagnosticBatches(): readonly AgentDiagnosticBatch[] {
    const batches = new Map<string, AgentDiagnostic[]>();
    for (const diagnostic of this.session()?.diagnostics ?? []) {
      const batch = batches.get(diagnostic.requestId) ?? [];
      batch.push(diagnostic);
      batches.set(diagnostic.requestId, batch);
    }
    return [...batches.entries()].map(([requestId, diagnostics]) => ({ requestId, diagnostics }));
  }

  send(): void { this.client.send(this.workspaces.activeId()); }
  stop(): void { this.client.stop(this.workspaces.activeId()); }

  composerKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.send();
    }
  }

  onScroll(): void {
    const element = this.transcript()?.nativeElement;
    if (element) this.followOutput = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
  }

  editLastPrompt(): void {
    const last = [...(this.session()?.messages ?? [])].reverse().find(message => message.role === 'user');
    if (last) this.setDraft(last.text);
    this.composer()?.nativeElement.focus();
  }

  pendingChoices(): readonly AgentChoice[] { return (this.session()?.choices ?? []).filter(choice => choice.status === 'pending'); }
  choose(choice: AgentChoice, optionId: string): void { this.client.submitChoice(this.workspaces.activeId(), choice, optionId); }
}
